"use client";

import * as React from "react";
import * as DialogPrimitive from "@radix-ui/react-dialog";
import { Cross2Icon } from "@radix-ui/react-icons";

import { cn } from "@/lib/utils";

const Dialog = DialogPrimitive.Root;

const DialogTrigger = DialogPrimitive.Trigger;

const DialogPortal = DialogPrimitive.Portal;

const DialogClose = DialogPrimitive.Close;

const DialogOverlay = React.forwardRef<
  React.ElementRef<typeof DialogPrimitive.Overlay>,
  React.ComponentPropsWithoutRef<typeof DialogPrimitive.Overlay> & {
    zIndex?: string;
    offsetHeader?: boolean;
  }
>(({ className, zIndex, offsetHeader, ...props }, ref) => (
  <DialogPrimitive.Overlay
    ref={ref}
    style={{ zIndex: zIndex || "var(--z-modal-backdrop)" }}
    className={cn(
      /* Updated to soft semi-transparent overlay backdrop with blur to match mockup */
      "fixed bg-black/15 backdrop-blur-sm data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0",
      offsetHeader ? "top-16 bottom-0 left-0 right-0" : "inset-0",
      className,
    )}
    {...props}
  />
));
DialogOverlay.displayName = DialogPrimitive.Overlay.displayName;

interface DialogContentProps extends React.ComponentPropsWithoutRef<
  typeof DialogPrimitive.Content
> {
  hideCloseButton?: boolean;
  zIndexBackdrop?: string;
  zIndexContent?: string;
  offsetHeader?: boolean;
}

const DialogContent = React.forwardRef<
  React.ElementRef<typeof DialogPrimitive.Content>,
  DialogContentProps
>(({ className, children, hideCloseButton = false, zIndexBackdrop, zIndexContent, offsetHeader = true, ...props }, ref) => (
  <DialogPortal>
    <DialogOverlay zIndex={zIndexBackdrop} offsetHeader={offsetHeader} />
    <DialogPrimitive.Content
      ref={ref}
      style={{ zIndex: zIndexContent || "var(--z-modal)" }}
      className={cn(
        /* 
          MODAL WRAPPER REFINEMENTS:
          - Swapped bg-background for translucent soft-gray 'bg-neutral-200/75'
          - Added enhanced frosted backdrop-blur-xl and saturate-140
          - Added smooth translucent white border 'border-white/40' for clean lines
        */
        "fixed grid gap-4 border border-white/40 bg-neutral-200/75 shadow-2xl duration-200 backdrop-blur-xl backdrop-saturate-140 data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0 data-[state=closed]:zoom-out-95 data-[state=open]:zoom-in-95 data-[state=closed]:slide-out-to-left-1/2 data-[state=closed]:slide-out-to-top-[48%] data-[state=open]:slide-in-from-left-1/2 data-[state=open]:slide-in-from-top-[48%]",
        
        // Mobile: full screen or offset from header
        offsetHeader ? "top-16 bottom-0 left-0 right-0 h-[calc(100dvh-4rem)] w-full rounded-none" : "inset-0 h-full w-full rounded-none",
        
        // Desktop: centered modal (unless offsetHeader)
        !offsetHeader && "md:left-[50%] md:top-[50%] md:translate-x-[-50%] md:translate-y-[-50%]",
        
        // Desktop with offsetHeader: centered horizontally but below header
        offsetHeader && "md:left-[50%] md:top-16 md:-translate-x-1/2 md:translate-y-0",
        "md:w-[calc(100vw-2rem)] md:max-w-lg",
        
        /* Desktop roundness refinement: Increased to match the heavy curve in mockup */
        "md:max-h-[calc(100dvh-2rem)] md:overflow-y-auto md:rounded-[calc(var(--radius)*2.5)]",
        className,
      )}
      {...props}
    >
      {children}
      {!hideCloseButton && (
        <DialogPrimitive.Close
          className="absolute right-4 top-4 z-10 h-11 w-11 touch-none rounded-none bg-gray-800/50 p-3 text-gray-400 transition-all duration-200
           hover:bg-gray-700/80 hover:text-white focus:outline-none focus:ring-2 focus:ring-cyan-500 focus:ring-offset-2 focus:ring-offset-gray-900 disabled:pointer-events-none"
        >
          <Cross2Icon className="h-5 w-5" />
          <span className="sr-only">Close</span>
        </DialogPrimitive.Close>
      )}
    </DialogPrimitive.Content>
  </DialogPortal>
));
DialogContent.displayName = DialogPrimitive.Content.displayName;

const DialogHeader = ({
  className,
  ...props
}: React.HTMLAttributes<HTMLDivElement>) => (
  <div
    className={cn(
      /*
        HEADER BAR REFINEMENTS:
        - Fills the top header area with near-solid off-white background block
        - Adds crisp border-bottom line and spacing matching the mockup look
      */
      "flex flex-col space-y-1.5 text-center sm:text-left bg-slate-50/95 border-b border-black/5 -mx-6 -mt-6 p-5 mb-2",
      className,
    )}
    {...props}
  />
);
DialogHeader.displayName = "DialogHeader";

const DialogFooter = ({
  className,
  ...props
}: React.HTMLAttributes<HTMLDivElement>) => (
  <div
    className={cn(
      "flex flex-col-reverse sm:flex-row sm:justify-end sm:space-x-2",
      className,
    )}
    {...props}
  />
);
DialogFooter.displayName = "DialogFooter";

const DialogTitle = React.forwardRef<
  React.ElementRef<typeof DialogPrimitive.Title>,
  React.ComponentPropsWithoutRef<typeof DialogPrimitive.Title>
>(({ className, ...props }, ref) => (
  <DialogPrimitive.Title
    ref={ref}
    className={cn(
      "text-lg font-semibold leading-none tracking-tight text-neutral-800",
      className,
    )}
    {...props}
  />
));
DialogTitle.displayName = DialogPrimitive.Title.displayName;

const DialogDescription = React.forwardRef<
  React.ElementRef<typeof DialogPrimitive.Description>,
  React.ComponentPropsWithoutRef<typeof DialogPrimitive.Description>
>(({ className, ...props }, ref) => (
  <DialogPrimitive.Description
    ref={ref}
    className={cn("text-sm text-muted-foreground", className)}
    {...props}
  />
));
DialogDescription.displayName = DialogPrimitive.Description.displayName;

export {
  Dialog,
  DialogPortal,
  DialogOverlay,
  DialogTrigger,
  DialogClose,
  DialogContent,
  DialogHeader,
  DialogFooter,
  DialogTitle,
  DialogDescription,
};